import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Button, Table, TableHead, TableBody, TableRow, TableCell,
  TableContainer, Paper, Switch, IconButton, Chip, Tooltip, Dialog,
  DialogTitle, DialogContent, DialogContentText, DialogActions, Alert,
  Stack, CircularProgress, FormControl, InputLabel, Select, MenuItem,
  Card, CardContent, FormControlLabel, Divider, Snackbar,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  NotificationsActive as AlertIcon,
  PhoneAndroid as PushIcon,
  Email as EmailIcon,
  CheckCircle as PassIcon,
  Cancel as FailIcon,
  Notifications as AnyIcon,
  Person as PersonIcon,
  Send as SendIcon,
  DirectionsCar as MotIcon,
  Warning as WarningIcon,
  CreditCard as DvlaIcon,
  LocalShipping as DeliveryIcon,
  Handshake as LoanIcon,
  PictureAsPdf as PdfIcon,
} from '@mui/icons-material';
import axios from 'axios';
import { API_BASE_URL } from '../../../config/api';
import AlertRuleDrawer from './AlertRuleDrawer';
import CommEmptyState from './shared/CommEmptyState';
import { commTabHeaderSx, commTableHeadRowSx } from './shared/communicationsStyles';

// ─── Types ────────────────────────────────────────────────────────────────────

type CommAlertCategory = 'all' | 'safety' | 'delivery' | 'loan' | 'mot' | 'dvla';

const CATEGORY_FILTER_OPTIONS: { value: CommAlertCategory; label: string; color: string; bg: string }[] = [
  { value: 'all',      label: 'All',             color: '#374151', bg: '#F3F4F6' },
  { value: 'safety',   label: 'Safety Checks',   color: '#1D4ED8', bg: '#EFF6FF' },
  { value: 'delivery', label: 'Delivery Checks',  color: '#065F46', bg: '#ECFDF5' },
  { value: 'loan',     label: 'Loan Checks',      color: '#6D28D9', bg: '#F5F3FF' },
  { value: 'mot',      label: 'MOT / Tax',        color: '#B45309', bg: '#FFFBEB' },
  { value: 'dvla',     label: 'DVLA',             color: '#9D174D', bg: '#FDF2F8' },
];

function inferCategory(trigger_event: string, rule_check_types?: string[] | null): CommAlertCategory {
  if (['mot_due_soon', 'mot_expired', 'tax_due_soon', 'tax_expired'].includes(trigger_event)) return 'mot';
  if (String(trigger_event).startsWith('dvla_')) return 'dvla';
  if (trigger_event === 'delivery_check_completed') return 'delivery';
  if (trigger_event === 'loan_check_completed') return 'loan';
  if (rule_check_types?.length) {
    const upper = rule_check_types.map((x: string) => x.toUpperCase());
    if (upper.some((x: string) => ['DELIVERY', 'OUTGOING_DELIVERY'].includes(x))) return 'delivery';
    if (upper.some((x: string) => ['LOAN', 'LOAN_RETURN'].includes(x))) return 'loan';
  }
  return 'safety';
}

interface AlertRule {
  rule_id: string;
  name: string;
  trigger_event: 'check_fail' | 'check_pass' | 'check_any' | 'delivery_check_completed' | 'loan_check_completed' | 'mot_due_soon' | 'mot_expired' | 'tax_due_soon' | 'tax_expired' | 'dvla_licence_expiring' | 'dvla_licence_expired' | 'dvla_card_expiring' | 'dvla_card_expired' | 'dvla_points_change' | 'dvla_new_endorsement' | 'dvla_licence_status_change';
  trigger_events?: Array<AlertRule['trigger_event']>;
  rule_check_types?: string[] | null;
  scope_type: 'all' | 'location' | 'asset_type' | 'asset_list' | 'template' | 'asset_template' | 'dvla_staff_list' | 'asset_tag';
  scope_location_id?: string;
  scope_location_name?: string;
  scope_asset_type_ids?: string[];
  scope_asset_ids?: string[];
  scope_staff_ids?: string[];
  scope_job_tag_ids?: string[];
  scope_template_id?: string;
  scope_template_name?: string;
  send_email: boolean;
  send_push: boolean;
  send_pdf_attachment?: boolean;
  enabled: boolean;
  recipient_count: number;
  created_at: string;
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

const TRIGGER_META: Record<AlertRule['trigger_event'], { label: string; color: string; bg: string; Icon: React.ElementType }> = {
  check_fail: { label: 'Check fails', color: '#DC2626', bg: '#FEE2E2', Icon: FailIcon },
  check_pass: { label: 'Check passes', color: '#059669', bg: '#D1FAE5', Icon: PassIcon },
  check_any: { label: 'Any check event', color: '#0288D1', bg: '#E0F2FE', Icon: AnyIcon },
  delivery_check_completed: { label: 'Delivery Check completed', color: '#2563EB', bg: '#DBEAFE', Icon: DeliveryIcon },
  loan_check_completed:     { label: 'Loan Check completed',     color: '#7C3AED', bg: '#EDE9FE', Icon: LoanIcon },
  mot_due_soon: { label: 'MOT due soon', color: '#D97706', bg: '#FEF3C7', Icon: MotIcon },
  mot_expired: { label: 'MOT expired', color: '#DC2626', bg: '#FEE2E2', Icon: MotIcon },
  tax_due_soon: { label: 'Road tax due soon', color: '#D97706', bg: '#FEF3C7', Icon: WarningIcon },
  tax_expired: { label: 'Road tax expired', color: '#DC2626', bg: '#FEE2E2', Icon: WarningIcon },
  dvla_licence_expiring:      { label: 'Licence expiring',         color: '#7C3AED', bg: '#EDE9FE', Icon: DvlaIcon },
  dvla_licence_expired:       { label: 'Licence expired',          color: '#DC2626', bg: '#FEE2E2', Icon: DvlaIcon },
  dvla_card_expiring:         { label: 'Photocard expiring',       color: '#7C3AED', bg: '#EDE9FE', Icon: DvlaIcon },
  dvla_card_expired:          { label: 'Photocard expired',        color: '#DC2626', bg: '#FEE2E2', Icon: DvlaIcon },
  dvla_points_change:         { label: 'Points increased',         color: '#B45309', bg: '#FEF3C7', Icon: WarningIcon },
  dvla_new_endorsement:       { label: 'New endorsement',          color: '#B45309', bg: '#FEF3C7', Icon: WarningIcon },
  dvla_licence_status_change: { label: 'Licence status changed',   color: '#DC2626', bg: '#FEE2E2', Icon: DvlaIcon },
};

function ScopeBadge({ rule }: { rule: AlertRule }) {
  if (rule.scope_type === 'all') return <Chip label="All assets" size="small" variant="outlined" />;
  if (rule.scope_type === 'location') return <Chip label={`📍 ${rule.scope_location_name || 'Location'}`} size="small" variant="outlined" color="primary" />;
  if (rule.scope_type === 'asset_type') return <Chip label={`${(rule.scope_asset_type_ids ?? []).length} asset type(s)`} size="small" variant="outlined" color="secondary" />;
  if (rule.scope_type === 'asset_list') return <Chip label={`${(rule.scope_asset_ids ?? []).length} asset(s)`} size="small" variant="outlined" color="info" />;
  if (rule.scope_type === 'template') return <Chip label={`📋 ${rule.scope_template_name || 'Template'}`} size="small" variant="outlined" color="warning" />;
  if (rule.scope_type === 'asset_template') return <Chip label={`🔗 Asset + Template`} size="small" variant="outlined" color="warning" />;
  if (rule.scope_type === 'dvla_staff_list') return <Chip label={`${(rule.scope_staff_ids ?? []).length} driver(s)`} size="small" variant="outlined" sx={{ borderColor: '#7C3AED', color: '#7C3AED' }} />;
  if (rule.scope_type === 'asset_tag') return <Chip label={`🏷️ ${(rule.scope_job_tag_ids ?? []).length} tag(s)`} size="small" variant="outlined" sx={{ borderColor: '#0D9488', color: '#0D9488' }} />;
  return null;
}

// ─── Primary-user default type ────────────────────────────────────────────────

interface PrimaryUserDefaults {
  enabled: boolean;
  trigger: 'check_fail' | 'check_pass' | 'check_any';
  sendEmail: boolean;
  sendPush: boolean;
}

// ─── Component ────────────────────────────────────────────────────────────────

const getAuthHeaders = () => {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const AlertsTab: React.FC = () => {
  const [rules, setRules] = useState<AlertRule[]>([]);
  const [rulesLoading, setRulesLoading] = useState(true);
  const [error, setError] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<CommAlertCategory>('all');
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editingRule, setEditingRule] = useState<AlertRule | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<AlertRule | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [testingId, setTestingId] = useState<string | null>(null);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success',
  });

  // Primary-user defaults
  const [puDefaults, setPuDefaults] = useState<PrimaryUserDefaults>({
    enabled: false, trigger: 'check_fail', sendEmail: true, sendPush: false,
  });
  const [puSaving, setPuSaving] = useState(false);

  /** Features & Access — used to show that MOT/DVLA comm alert types stay available when modules are off */
  const [commAlertBizFeatures, setCommAlertBizFeatures] = useState<{
    motTaxEnabled: boolean;
    dvlaEnabled: boolean;
    locationsEnabled: boolean;
    useSiteTerminology: boolean;
  } | null>(null);

  const fetchRules = useCallback(async () => {
    setRulesLoading(true); setError('');
    try {
      const res = await axios.get(`${API_BASE_URL}/communication-alerts`, { headers: getAuthHeaders() });
      setRules(res.data?.data || []);
    } catch {
      setError('Failed to load alert rules');
    } finally {
      setRulesLoading(false);
    }
  }, []);

  const fetchPuDefaults = useCallback(async () => {
    try {
      const res = await axios.get(`${API_BASE_URL}/communication-alerts/primary-user-defaults`, {
        headers: getAuthHeaders(),
      });
      if (res.data?.data) setPuDefaults(res.data.data);
    } catch { /* non-fatal */ }
  }, []);

  const savePuDefaults = async (patch: Partial<PrimaryUserDefaults>) => {
    const next = { ...puDefaults, ...patch };
    setPuDefaults(next);
    setPuSaving(true);
    try {
      await axios.put(`${API_BASE_URL}/communication-alerts/primary-user-defaults`, next, {
        headers: getAuthHeaders(),
      });
    } catch {
      // revert on error
      setPuDefaults(puDefaults);
    } finally {
      setPuSaving(false);
    }
  };

  useEffect(() => { fetchRules(); fetchPuDefaults(); }, [fetchRules, fetchPuDefaults]);

  useEffect(() => {
    const bid = localStorage.getItem('businessId');
    const token = localStorage.getItem('token');
    if (!bid || !token) return;
    let cancelled = false;
    axios
      .get(`${API_BASE_URL}/businesses/${bid}`, { headers: { Authorization: `Bearer ${token}` } })
      .then((res) => {
        if (cancelled) return;
        setCommAlertBizFeatures({
          motTaxEnabled: res.data.allow_mot_tax_tab === 1 || res.data.allow_mot_tax_tab === true,
          dvlaEnabled: res.data.allow_dvla !== 0 && res.data.allow_dvla !== false,
          locationsEnabled: res.data.allow_locations !== 0 && res.data.allow_locations !== false,
          useSiteTerminology: res.data.use_site_terminology === 1 || res.data.use_site_terminology === true,
        });
      })
      .catch(() => {
        if (!cancelled) setCommAlertBizFeatures(null);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const handleTest = async (rule: AlertRule) => {
    setTestingId(rule.rule_id);
    try {
      const res = await axios.post(
        `${API_BASE_URL}/communication-alerts/${rule.rule_id}/test`,
        {},
        { headers: getAuthHeaders() },
      );
      setSnackbar({
        open: true,
        message: res.data?.message || 'Test sent',
        severity: 'success',
      });
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { error?: string } } })?.response?.data?.error
        || 'Failed to send test';
      setSnackbar({ open: true, message: msg, severity: 'error' });
    } finally {
      setTestingId(null);
    }
  };

  const handleToggle = async (rule: AlertRule) => {
    const next = !rule.enabled;
    setRules(prev => prev.map(r => r.rule_id === rule.rule_id ? { ...r, enabled: next } : r));
    try {
      await axios.patch(`${API_BASE_URL}/communication-alerts/${rule.rule_id}/toggle`, { enabled: next }, {
        headers: getAuthHeaders(),
      });
    } catch {
      setRules(prev => prev.map(r => r.rule_id === rule.rule_id ? { ...r, enabled: !next } : r));
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await axios.delete(`${API_BASE_URL}/communication-alerts/${deleteTarget.rule_id}`, {
        headers: getAuthHeaders(),
      });
      setRules(prev => prev.filter(r => r.rule_id !== deleteTarget.rule_id));
      setDeleteTarget(null);
    } catch {
      setError('Failed to delete alert rule');
    } finally {
      setDeleting(false);
    }
  };

  const handleEdit = async (rule: AlertRule) => {
    try {
      const res = await axios.get(`${API_BASE_URL}/communication-alerts/${rule.rule_id}`, {
        headers: getAuthHeaders(),
      });
      setEditingRule(res.data?.data || rule);
    } catch {
      setEditingRule(rule);
    }
    setDrawerOpen(true);
  };

  return (
    <Box>

      {/* ── Primary User Defaults ─────────────────────────────────────────── */}
      <Card variant="outlined" sx={{ mb: 4, borderColor: puDefaults.enabled ? 'primary.main' : 'divider' }}>
        <CardContent>
          <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <PersonIcon color={puDefaults.enabled ? 'primary' : 'disabled'} />
              <Box>
                <Typography variant="subtitle1" fontWeight={700}>Primary User Alerts (Default)</Typography>
                <Typography variant="body2" color="text.secondary">
                  Automatically alert the assigned primary user of an asset whenever a check event occurs —
                  no need to create an individual rule per user.
                </Typography>
              </Box>
            </Box>
            <FormControlLabel
              control={
                <Switch
                  checked={puDefaults.enabled}
                  onChange={e => savePuDefaults({ enabled: e.target.checked })}
                  disabled={puSaving}
                />
              }
              label={<Typography variant="body2" fontWeight={600}>{puDefaults.enabled ? 'On' : 'Off'}</Typography>}
              sx={{ ml: 0 }}
            />
          </Stack>

          {puDefaults.enabled && (
            <>
              <Divider sx={{ my: 2 }} />
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="flex-start" flexWrap="wrap">
                <FormControl size="small" sx={{ minWidth: 200 }}>
                  <InputLabel>Fire when</InputLabel>
                  <Select
                    value={puDefaults.trigger}
                    label="Fire when"
                    onChange={e => savePuDefaults({ trigger: e.target.value as PrimaryUserDefaults['trigger'] })}
                    disabled={puSaving}
                  >
                    <MenuItem value="check_fail">Check fails</MenuItem>
                    <MenuItem value="check_pass">Check passes</MenuItem>
                    <MenuItem value="check_any">Any check event</MenuItem>
                  </Select>
                </FormControl>

                <Stack direction="row" spacing={1} alignItems="center" sx={{ pt: 0.5 }}>
                  <Tooltip title="Send email to primary user">
                    <FormControlLabel
                      control={
                        <Switch
                          size="small"
                          checked={puDefaults.sendEmail}
                          onChange={e => savePuDefaults({ sendEmail: e.target.checked })}
                          disabled={puSaving}
                        />
                      }
                      label={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <EmailIcon sx={{ fontSize: 16, color: '#0288D1' }} />
                          <Typography variant="body2">Email</Typography>
                        </Box>
                      }
                    />
                  </Tooltip>
                  <Tooltip title="Send push notification to primary user">
                    <FormControlLabel
                      control={
                        <Switch
                          size="small"
                          checked={puDefaults.sendPush}
                          onChange={e => savePuDefaults({ sendPush: e.target.checked })}
                          disabled={puSaving}
                        />
                      }
                      label={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <PushIcon sx={{ fontSize: 16, color: '#7C3AED' }} />
                          <Typography variant="body2">Push</Typography>
                        </Box>
                      }
                    />
                  </Tooltip>
                  {puSaving && <CircularProgress size={16} sx={{ ml: 1 }} />}
                </Stack>
              </Stack>
            </>
          )}
        </CardContent>
      </Card>

      {/* ── Alert Rules header ────────────────────────────────────────────── */}
      <Box sx={commTabHeaderSx}>
        <Box>
          <Typography variant="h5" fontWeight={700}>Alert Rules</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Instant alerts sent when a check passes, fails, or completes on a matching asset or template,
            when a Delivery Check or Loan Check goes out, or when a vehicle MOT / road tax is due soon or expired.
            Alerts fire the moment a status changes — with email and/or mobile push notification.
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => { setEditingRule(null); setDrawerOpen(true); }}
          sx={{ flexShrink: 0, ml: 2 }}
        >
          New Alert
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      {/* Category filter chips */}
      {!rulesLoading && rules.length > 0 && (
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
          {CATEGORY_FILTER_OPTIONS.map(opt => {
            const active = categoryFilter === opt.value;
            return (
              <Chip
                key={opt.value}
                label={opt.label}
                size="small"
                variant={active ? 'filled' : 'outlined'}
                onClick={() => setCategoryFilter(opt.value)}
                sx={{
                  cursor: 'pointer',
                  fontWeight: active ? 700 : 400,
                  bgcolor: active ? opt.bg : undefined,
                  color: active ? opt.color : undefined,
                  borderColor: active ? opt.color : undefined,
                }}
              />
            );
          })}
        </Stack>
      )}

      {rulesLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      ) : rules.length === 0 ? (
        <CommEmptyState
          icon={<AlertIcon />}
          heading="No alert rules yet"
          description="Create an alert rule to get notified the instant a check passes, fails, or completes — or when a vehicle MOT or road tax is due soon or expired — scoped to any combination of assets, locations, asset types, or templates."
          ctaLabel="Create first alert rule"
          onCta={() => { setEditingRule(null); setDrawerOpen(true); }}
        />
      ) : (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow sx={commTableHeadRowSx}>
                <TableCell>Alert Rule</TableCell>
                <TableCell>Trigger</TableCell>
                <TableCell>Scope</TableCell>
                <TableCell align="center">Recipients</TableCell>
                <TableCell align="center">Channels</TableCell>
                <TableCell align="center">Enabled</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rules.filter(rule => {
                if (categoryFilter === 'all') return true;
                return inferCategory(rule.trigger_event, rule.rule_check_types) === categoryFilter;
              }).map(rule => {
                const meta = TRIGGER_META[rule.trigger_event];
                const TrigIcon = meta.Icon;
                return (
                  <TableRow key={rule.rule_id} hover sx={{ opacity: rule.enabled ? 1 : 0.5 }}>
                    <TableCell>
                      <Typography variant="body2" fontWeight={600}>{rule.name}</Typography>
                    </TableCell>
                    <TableCell>
                      {rule.trigger_events && rule.trigger_events.length > 1 ? (
                        <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                          {rule.trigger_events.map(te => {
                            const m = TRIGGER_META[te];
                            const TI = m.Icon;
                            return (
                              <Box key={te} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <TI sx={{ fontSize: 13, color: m.color }} />
                                <Typography variant="caption" fontWeight={600} sx={{ color: m.color }}>{m.label}</Typography>
                              </Box>
                            );
                          })}
                        </Stack>
                      ) : (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                          <TrigIcon sx={{ fontSize: 14, color: meta.color }} />
                          <Typography variant="caption" fontWeight={600} sx={{ color: meta.color }}>{meta.label}</Typography>
                        </Box>
                      )}
                    </TableCell>
                    <TableCell><ScopeBadge rule={rule} /></TableCell>
                    <TableCell align="center">
                      <Chip label={`${rule.recipient_count} recipient${rule.recipient_count !== 1 ? 's' : ''}`} size="small" variant="outlined" />
                    </TableCell>
                    <TableCell align="center">
                      <Stack direction="row" spacing={0.5} justifyContent="center">
                        {rule.send_email && (
                          <Tooltip title="Email">
                            <EmailIcon sx={{ fontSize: 16, color: '#0288D1' }} />
                          </Tooltip>
                        )}
                        {rule.send_email && rule.send_pdf_attachment && (
                          <Tooltip title="PDF report attached">
                            <PdfIcon sx={{ fontSize: 16, color: '#DC2626' }} />
                          </Tooltip>
                        )}
                        {rule.send_push && (
                          <Tooltip title="Push notification">
                            <PushIcon sx={{ fontSize: 16, color: '#7C3AED' }} />
                          </Tooltip>
                        )}
                        {!rule.send_email && !rule.send_push && (
                          <Typography variant="caption" color="text.disabled">—</Typography>
                        )}
                      </Stack>
                    </TableCell>
                    <TableCell align="center">
                      <Switch
                        size="small"
                        checked={rule.enabled}
                        onChange={() => handleToggle(rule)}
                      />
                    </TableCell>
                    <TableCell align="right" sx={{ verticalAlign: 'top' }}>
                      <Stack spacing={0.75} alignItems="flex-end">
                        <Stack direction="row" spacing={0.5} justifyContent="flex-end" alignItems="center">
                          <Tooltip title="Edit">
                            <IconButton size="small" onClick={() => handleEdit(rule)} disabled={testingId !== null}>
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete">
                            <IconButton size="small" color="error" onClick={() => setDeleteTarget(rule)} disabled={testingId !== null}>
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                        <Tooltip title="Sends a sample using this rule’s email / push settings and resolved recipients">
                          <span>
                            <Button
                              size="small"
                              variant="outlined"
                              fullWidth
                              startIcon={
                                testingId === rule.rule_id ? (
                                  <CircularProgress size={14} color="inherit" />
                                ) : (
                                  <SendIcon sx={{ fontSize: 16 }} />
                                )
                              }
                              onClick={() => handleTest(rule)}
                              disabled={testingId !== null}
                              sx={{ textTransform: 'none', fontSize: '0.75rem', minWidth: 100, maxWidth: 120 }}
                            >
                              {testingId === rule.rule_id ? 'Sending…' : 'Test alert'}
                            </Button>
                          </span>
                        </Tooltip>
                      </Stack>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Info callout */}
      {!rulesLoading && rules.length > 0 && (
        <Alert severity="info" sx={{ mt: 2 }}>
          Check alerts include asset name, location, check name, who completed it, and timestamp — firing instantly on status change.
          MOT / road tax alerts fire daily at 07:00 (UK time) for vehicles with expired or soon-to-expire certificates.
        </Alert>
      )}

      {/* Drawer */}
      <AlertRuleDrawer
        open={drawerOpen}
        rule={editingRule}
        onClose={() => { setDrawerOpen(false); setEditingRule(null); }}
        onSaved={fetchRules}
        businessMotTaxEnabled={commAlertBizFeatures?.motTaxEnabled ?? true}
        businessDvlaEnabled={commAlertBizFeatures?.dvlaEnabled ?? true}
        businessLocationsEnabled={commAlertBizFeatures?.locationsEnabled ?? true}
        locationLabel={commAlertBizFeatures?.useSiteTerminology ? 'Site' : 'Location'}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Delete dialog */}
      <Dialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)}>
        <DialogTitle>Delete alert rule?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            <strong>"{deleteTarget?.name}"</strong> will be permanently deleted and will no longer fire.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)}>Cancel</Button>
          <Button color="error" onClick={handleDelete} disabled={deleting}>
            {deleting ? 'Deleting…' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AlertsTab;
